import type { TransferTransactionInfo } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import type { Transaction } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { TransferDirection } from '@safe-global/store/gateway/types'
import NamedAddressInfo from '@/components/common/NamedAddressInfo'
import { TransferTx } from '@/components/transactions/TxInfo'
import { isTxQueued } from '@/utils/transaction-guards'
import { Box, Stack, Typography } from '@mui/material'

import TransferActions from '@/components/transactions/TxDetails/TxData/Transfer/TransferActions'
import MaliciousTxWarning from '@/components/transactions/MaliciousTxWarning'
import { ImitationTransactionWarning } from '@/components/transactions/ImitationTransactionWarning'
import TokenAmount from '@/components/common/TokenAmount'
import { type NativeToken, type Erc20Token } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import FiatValue from '@/components/common/FiatValue'
import useTransferFiatValue from './useTransferFiatValue'

type TransferTxInfoProps = {
  txInfo: TransferTransactionInfo
  txStatus: Transaction['txStatus']
  trusted: boolean
  imitation: boolean
}

const TransferTxInfoMain = ({ txInfo, txStatus, trusted, imitation }: TransferTxInfoProps) => {
  const { direction } = txInfo
  const isQueued = isTxQueued(txStatus)
  const fiatValue = useTransferFiatValue(txInfo.transferInfo, isQueued)

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={{ '& b': { fontWeight: 'normal' } }}>
      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 40 }}>
        {direction === TransferDirection.INCOMING ? 'Received' : isQueued ? 'Send' : 'Sent'}
      </Typography>
      <TransferTx info={txInfo} omitSign preciseAmount iconSize={32} />
      {fiatValue != null && (
        <Typography variant="body2" color="text.secondary" component="span">
          (<FiatValue value={fiatValue} />)
        </Typography>
      )}
      {!trusted && !imitation && <MaliciousTxWarning />}
    </Box>
  )
}

const TransferTxInfo = ({ txInfo, txStatus, trusted, imitation }: TransferTxInfoProps) => {
  const { direction } = txInfo
  const address = direction.toUpperCase() === TransferDirection.INCOMING ? txInfo.sender : txInfo.recipient
  const directionLabel = direction === TransferDirection.INCOMING ? 'From' : 'To'

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <TransferTxInfoMain txInfo={txInfo} txStatus={txStatus} trusted={trusted} imitation={imitation} />

      <Box display="flex" alignItems="center" gap={1} width="100%" sx={{ '& .ethHashInfo-name': { fontWeight: 700 } }}>
        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 40, whiteSpace: 'nowrap' }}>
          {directionLabel}
        </Typography>
        <NamedAddressInfo
          address={address.value}
          name={address.name}
          customAvatar={address.logoUri}
          shortAddress={false}
          hasExplorer
          showCopyButton
          showPrefix={false}
          avatarSize={32}
          trusted={trusted && !imitation}
        >
          <TransferActions address={address.value} txInfo={txInfo} trusted={trusted} />
        </NamedAddressInfo>
      </Box>
      {imitation && <ImitationTransactionWarning />}
    </Box>
  )
}

export const InlineTransferTxInfo = ({
  value,
  tokenInfo,
  recipient,
}: {
  value: string
  tokenInfo: Erc20Token | NativeToken
  recipient: string
}) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography>Send</Typography>
      <TokenAmount
        value={value}
        decimals={tokenInfo.decimals}
        logoUri={tokenInfo.logoUri}
        tokenSymbol={tokenInfo.symbol}
        iconSize={16}
      />
      <Typography>to</Typography>
      <NamedAddressInfo address={recipient} copyAddress={false} shortAddress={true} onlyName avatarSize={16} />
    </Stack>
  )
}

export default TransferTxInfo
